<script setup lang="ts">
import { computed } from 'vue'

import type { ReportRow } from '../../models/report.types'
import { toTableColumns } from '../../service/toTableColumns'

import type { MatrixTableProps } from './types'

import { DataTable, type DataTableProps, type SortState, type TableColumn } from '@/shared/ui'
import { MetricView } from '@/shared/ui/metric-view'

const props = withDefaults(defineProps<MatrixTableProps>(), {
  state: 'ready',
  sort: null,
  draggable: false,
  sortCycle: 'binary',
  initialSortDirection: 'asc',
  allowColumnResize: true,
  channel: '',
  enableFreeze: false,
  expandable: false,
})

const emit = defineEmits<{
  'update:sort': [sort: SortState]
  reorder: [columns: TableColumn<ReportRow>[]]
  resize: [columns: TableColumn<ReportRow>[]]
  freeze: [field: string, frozen: boolean]
  'comparison-expand': [field: string, expanded: boolean]
  'load-more': []
}>()

const tableColumns = computed(() =>
  toTableColumns(props.columns).map((column) => ({
    ...column,
    resizable: props.allowColumnResize && column.resizable !== false,
  })),
)
const reportColumnsByField = computed(
  () => new Map(props.columns.map((column) => [column.field, column] as const)),
)

function defaultRowKey(_row: ReportRow, index: number): number {
  return index
}

const dataTableProps = computed<DataTableProps<ReportRow>>(() => ({
  rows: props.rows,
  ...(props.footerRows === undefined ? {} : { footerRows: props.footerRows }),
  columns: tableColumns.value,
  ...(props.columnGroups === undefined ? {} : { columnGroups: props.columnGroups }),
  ariaLabel: props.ariaLabel,
  state: props.state,
  skeletonRows: 10,
  refreshing: props.refreshing,
  sort: props.sort,
  draggable: props.draggable,
  freezeEnabled: props.enableFreeze,
  ...(props.freezeLabel === undefined ? {} : { freezeLabel: props.freezeLabel }),
  rowKey: props.rowKey ?? defaultRowKey,
  clientSort: false,
  sortCycle: props.sortCycle,
  initialSortDirection: props.initialSortDirection,
  expandable: props.expandable,
  ...(props.getChildren === undefined ? {} : { getChildren: props.getChildren }),
  ...(props.expandLabel === undefined ? {} : { expandLabel: props.expandLabel }),
  ...(props.collapseLabel === undefined ? {} : { collapseLabel: props.collapseLabel }),
  ...(props.maxHeight === undefined ? {} : { maxHeight: props.maxHeight }),
  ...(props.totalRows === undefined ? {} : { totalRows: props.totalRows }),
  ...(props.comparisonExpandableFields === undefined
    ? {}
    : { columnExpandableFields: props.comparisonExpandableFields }),
  ...(props.comparisonExpandedFields === undefined
    ? {}
    : { columnExpandedFields: props.comparisonExpandedFields }),
  ...(props.comparisonExpandLabel === undefined
    ? {}
    : {
        columnExpandLabel: (column: TableColumn<ReportRow>, expanded: boolean) => {
          const reportColumn = reportColumnsByField.value.get(String(column.field))
          return reportColumn ? props.comparisonExpandLabel!(reportColumn, expanded) : column.header
        },
      }),
}))
</script>

<template>
  <DataTable
    v-bind="dataTableProps"
    @update:sort="emit('update:sort', $event)"
    @reorder="emit('reorder', $event)"
    @resize="emit('resize', $event)"
    @freeze="
      (column: TableColumn<ReportRow>, frozen: boolean) =>
        emit('freeze', String(column.field), frozen)
    "
    @expand-column="
      (column: TableColumn<ReportRow>, expanded: boolean) =>
        emit('comparison-expand', String(column.field), expanded)
    "
    @load-more="emit('load-more')"
  >
    <template #empty>
      <slot name="empty" />
    </template>
    <template #error="slotProps">
      <slot name="error" v-bind="slotProps" />
    </template>
    <template
      v-for="column in tableColumns"
      :key="`header-${column.field}`"
      #[`header-${column.field}`]="header"
    >
      <slot :name="`header-${column.field}`" v-bind="header">
        <slot name="header" v-bind="header">
          <MetricView
            v-if="reportColumnsByField.get(String(column.field))"
            :column="reportColumnsByField.get(String(column.field))!"
            :channel="channel"
          />
          <template v-else>{{ header.column.header }}</template>
        </slot>
      </slot>
    </template>
    <template v-for="column in tableColumns" :key="column.field" #[`cell-${column.field}`]="cell">
      <slot :name="`cell-${column.field}`" v-bind="cell">
        <slot name="cell" v-bind="cell">{{ cell.value }}</slot>
      </slot>
    </template>
    <template
      v-for="column in tableColumns"
      :key="`footer-${column.field}`"
      #[`footer-${column.field}`]="footer"
    >
      <slot :name="`footer-${column.field}`" v-bind="footer">
        <slot name="footer" v-bind="footer">{{ footer.value }}</slot>
      </slot>
    </template>
  </DataTable>
</template>
